import {B2BoxType, B2Site} from "../../../data/client.gen";
import React, {useMemo} from "react";
import {PlainTime, TemporalFormat, TemporalRangeFormat, ZonedDateTime} from "@variocube/app-ui";
import {Box, Button, Stack, Typography} from "@mui/material";
import {useLocalization} from "../../../i18n";
import {BoxedInfo} from "../../../component/boxed-info";
import {DisplayCurrency, notBlank} from "../../../utils";
import {getZoneDateTimeNow} from "../../../dates-utils";

interface OrderOverviewProps {
	site: B2Site;
	baseTime: ZonedDateTime;
	onNext: () => void;
	box: B2BoxType|undefined;
}

export function OrderOverview({site, box, baseTime, onNext}: OrderOverviewProps) {
	const {t, language} = useLocalization();
	const {
		currency = 'EUR',
		alwaysAccessible,
		accessibleFrom,
		accessibleUntil,
		zoneId = 'UTC',
	} = site;

	const now = getZoneDateTimeNow(zoneId);
	const isBeforeCurrentTime = useMemo(() =>
		ZonedDateTime.compare(baseTime, now) < 1
		, [baseTime])

	return (
		<>
			<Typography textAlign="center" variant="h1" gutterBottom>{t('orders.overview.title')}</Typography>
			<BoxedInfo
				label={t('orders.overview.eventDate')}
				content={
					<Typography>
						{isBeforeCurrentTime
							? <TemporalRangeFormat locale={language} from={baseTime} until={now} />
							: <TemporalFormat locale={language} value={baseTime} />
						}
					</Typography>
				}
			/>
			<BoxedInfo
				label={t('sites.accessibility')}
				content={
					<Typography>
						{alwaysAccessible && <span>24/7</span>}
						{!alwaysAccessible && notBlank(accessibleFrom) && notBlank(accessibleUntil) && (
							<TemporalRangeFormat
								from={PlainTime.from(accessibleFrom as string)}
								until={PlainTime.from(accessibleUntil as string)}
							/>
						)}
					</Typography>
				}
			/>
			<BoxedInfo
				label={t('boxes.type')}
				content={(
					<>
						<Stack
							direction="row"
							justifyContent="center"
							gap={2}
						>
							<div>
								<Typography variant="body2" textAlign="center"><strong>{t('boxes.dimensions.width')}</strong></Typography>
								<Typography variant="body2" textAlign="center">{(box?.width ?? 0) / 10}cm</Typography>
							</div>
							<div>
								<Typography variant="body2" textAlign="center"><strong>{t('boxes.dimensions.height')}</strong></Typography>
								<Typography variant="body2" textAlign="center">{(box?.height ?? 0) / 10}cm</Typography>
							</div>
							<div>
								<Typography variant="body2" textAlign="center"><strong>{t('boxes.dimensions.depth')}</strong></Typography>
								<Typography variant="body2" textAlign="center">{(box?.depth ?? 0) / 10}cm</Typography>
							</div>
						</Stack>
					</>
				)}
			/>
			<BoxedInfo
				label={t('orders.total')}
				content={
					<Typography>{currency} <DisplayCurrency amount={box?.price ?? 0} /></Typography>
				}
			/>
			<Box my={1} flexGrow={1} />
			<Button
				variant="contained"
				color="primary"
				disableElevation
				onClick={onNext}
			>
				{t('orders.overview.cta')}
			</Button>
		</>
	)
}
